import { useQuery } from '@tanstack/react-query';

import { settingsApi, type Capabilities } from '@/features/settings/api';
import { keys } from '@/lib/query/keys';
import { MEDIA_FALLBACK_MAX_BYTES } from '@/lib/config';

/**
 * How large a file this deployment will actually accept, per kind.
 *
 * ── The defect (QA F-14) ────────────────────────────────────────────────────
 *
 * The dropzone advertised `max 32 MB` and pre-validated against the same number, both from
 * a hardcoded `MEDIA_MAX_BYTES`. No value the server publishes is 32 MB, and it is not the
 * limit for any kind of file the console accepts: images are capped at 15 MB. So a 19 MB
 * PNG passed the client guard, was staged, was fully transferred, and was then refused
 * with `413 payload_too_large — File exceeds the 15 MB limit.` The failure was graceful —
 * the server's message is specific and the console shows it with a request id — but the
 * editor had already spent the upload, and the hint still said 32 MB the next time.
 *
 * Rejecting client-side before the transfer is the whole point of a pre-flight check: it
 * saves the upload, and it is the only version of this check that helps somebody on a slow
 * link. That only works if the number is the server's.
 *
 * ── The mapping is the server's, restated once ──────────────────────────────
 *
 * `be-svet`'s `media.Service.checkSize` (internal/media/service.go):
 *
 *     image, svg          -> MAX_IMAGE_BYTES   (15 MB by default)
 *     pdf, doc, anything  -> MAX_PDF_BYTES     (25 MB by default)
 *
 * and both numbers are published as `limits.max_image_bytes` / `limits.max_pdf_bytes` in
 * `GET /admin/capabilities` precisely so a client does not have to hardcode them. A single
 * console-wide number cannot be right for both, which is why this returns a limit per kind
 * rather than one cap.
 *
 * `limits.max_upload_bytes` is deliberately NOT used as a per-file cap: it is the ROUTE's
 * body cap (`max(image, pdf, cv) + 2 MB`), so validating against it would reproduce the
 * finding one number lower — a 20 MB image would pass the guard and be refused at 15.
 */

/** The kinds `be-svet` distinguishes when it checks a size. */
export type MediaKind = 'image' | 'svg' | 'pdf' | 'doc' | 'other';

/** Extension -> kind, over `ACCEPTED_EXTENSIONS`. Sniffing by extension is what the
 *  pre-flight check has; the server checks magic bytes and has the last word. */
const KIND_BY_EXTENSION: Record<string, MediaKind> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  webp: 'image',
  gif: 'image',
  svg: 'svg',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'doc',
  mp4: 'other',
  webm: 'other',
};

export function kindForFile(file: { name: string }): MediaKind {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return KIND_BY_EXTENSION[ext] ?? 'other';
}

export interface UploadLimits {
  /** Images and SVG. */
  image: number;
  /** PDF, DOC, and — as on the server — everything else. */
  document: number;
  /** True once the server's own numbers are in hand, false while the fallbacks are in use. */
  fromServer: boolean;
}

/**
 * The fallbacks, used until `GET /admin/capabilities` resolves and on a server too old to
 * publish `limits`.
 *
 * Both are the SMALLEST server cap rather than the largest, which is the opposite of what
 * the hardcoded 32 MB did: a guard that is too strict refuses a file the server would have
 * taken and says so instantly, and a guard that is too loose spends the editor's upload
 * before the server refuses it. Only one of those wastes anybody's time.
 */
const FALLBACK: UploadLimits = {
  image: MEDIA_FALLBACK_MAX_BYTES,
  document: MEDIA_FALLBACK_MAX_BYTES,
  fromServer: false,
};

/** Resolve the per-kind caps from a capabilities document (or the fallbacks without one). */
export function limitsFrom(capabilities: Capabilities | undefined): UploadLimits {
  const image = capabilities?.limits?.max_image_bytes;
  const document = capabilities?.limits?.max_pdf_bytes;
  if (!image || !document) return FALLBACK;
  return { image, document, fromServer: true };
}

/** The cap that will actually be applied to this file, in bytes. */
export function limitForKind(kind: MediaKind, limits: UploadLimits): number {
  return kind === 'image' || kind === 'svg' ? limits.image : limits.document;
}

/**
 * The deployment's upload limits.
 *
 * Same query key as the settings screen and the locale hook, so this shares one cached
 * document with them rather than adding a request: capabilities describe the deployment
 * and change only when it is redeployed.
 */
export function useUploadLimits(): UploadLimits {
  const { data } = useQuery({
    queryKey: keys.settings.capabilities(),
    queryFn: () => settingsApi.getCapabilities(),
    staleTime: 5 * 60_000,
  });
  return limitsFrom(data);
}

/**
 * Why a file would be refused before it is sent, or null when it would not.
 *
 * Checked against the limit the SERVER applies to this kind of file (QA F-14) and
 * the extensions it accepts. One function for every upload control — the library
 * dropzone and a gallery's "Add photos" — so the two cannot disagree about which
 * file is acceptable.
 */
export type UploadIssue = { kind: 'type'; ext: string } | { kind: 'size'; limit: number };

export function uploadIssue(
  file: { name: string; size: number },
  limits: UploadLimits,
  accepted: readonly string[],
): UploadIssue | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!accepted.includes(ext)) return { kind: 'type', ext };
  const limit = limitForKind(kindForFile(file), limits);
  if (file.size > limit) return { kind: 'size', limit };
  return null;
}
